import type { FileReviewInDb } from '@/api';
import { useFileReviewStore } from '@/stores/fileReviewStore';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

const enum Routes {
  Table = 'review-table',
  Detail = 'review-contract',
}

function resolveReviewRoute(reviewUid: string, files: FileReviewInDb[]): RouteLocationRaw {
  const isSingleFile = files.length === 1 && files[0].static_vault_file_copy.uid;
  if (isSingleFile) {
    return {
      name: Routes.Detail,
      params: { reviewId: reviewUid },
      query: {
        fileUid: files[0].static_vault_file_copy.uid,
        source: 'redirect',
      },
    };
  }

  return { name: Routes.Table, params: { id: reviewUid } };
}

/**
 * Returns a function you call with a review UID to navigate
 * to its review page (contract vs table) using named routes.
 */
export function useReviewNavigator(): (reviewUid: string) => Promise<void> {
  const router = useRouter();
  const store = useFileReviewStore();
  const toast = useGaiaToast();

  async function navigateToReview(reviewUid: string): Promise<void> {
    try {
      let files = store.fileReviewsFor(reviewUid);

      if (files.length === 0) {
        await store.searchFileReviews(reviewUid, { text: '' });
        files = store.fileReviewsFor(reviewUid);
      }

      const route = resolveReviewRoute(reviewUid, files);
      await router.push(route);
    } catch (error) {
      toast.error('Could not open the review. Please try again.');
      logger.error('Error navigating to review', error);
    }
  }

  return navigateToReview;
}
